import {
  AppWrapper,
  ResizeGrip,
  ResizeGripIndicator,
  ResizeHandle,
  Workspace,
  WorkspaceWrapper,
} from './styles';
import { FileProvider, FileView } from '@morph-mapper/file-viewer';
import { Outlet } from 'react-router';
import { useStore } from '@/store';
import { useGlobalEntryListener, useActiveFile } from '@/hooks';
import { NavigateModal } from '../navigate-modal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useScrollPosition } from '@/hooks';
import { LayoutDirection } from '@/types';
import { useGraphBatchProcess } from '../../../graph-editor/hooks/use-graph-batch-process';
import { SchemaVariant } from '@morph-mapper/types';
import { getFileKey } from '@/utils';
import { UpdateDiffView } from './diff-view/update-diff-view';

export const Overview = () => {
  const [
    direction,
    setDirection,
    textContent,
    variant,
    file,
    text,
    isUpdateDiff,
  ] = useStore(({ ui, config: c }) => [
    ui.layoutDirection,
    ui.setLayoutDirection,
    c.textContent,
    c.getVariant(),
    c.file,
    c.getTextContent(),
    c.isUpdateDiff,
  ]);

  const workspaceRef = useRef<HTMLDivElement>(null);
  useScrollPosition(workspaceRef);

  const fileKey = getFileKey(file);

  return (
    <FileProvider
      key={fileKey}
      file={file}
      variant={variant}
      textContent={text}
    >
      <OverviewContent
        textContent={textContent}
        direction={direction}
        setDirection={setDirection}
        workspaceRef={workspaceRef}
        isUpdateDiff={isUpdateDiff}
      />
    </FileProvider>
  );
};

interface OverviewContentProps {
  textContent: string | object | null | undefined;
  direction: LayoutDirection;
  setDirection: (direction: LayoutDirection) => void;
  workspaceRef: React.RefObject<HTMLDivElement>;
  isUpdateDiff: boolean;
}

const OverviewContent = ({
  textContent,
  direction,
  setDirection,
  workspaceRef,
  isUpdateDiff,
}: OverviewContentProps) => {
  useGlobalEntryListener();
  useActiveFile();
  useGraphBatchProcess();
  const [splitRatio, setSplitRatio] = useState(0.5);
  const dragStateRef = useRef<{
    start: number;
    size: number;
    ratio: number;
  } | null>(null);

  const clampRatio = (nextRatio: number) =>
    Math.min(0.8, Math.max(0.2, nextRatio));

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const axis =
        direction === LayoutDirection.Row ? event.clientX : event.clientY;
      const delta = axis - dragState.start;
      const nextRatio =
        (dragState.ratio * dragState.size + delta) / dragState.size;
      setSplitRatio(clampRatio(nextRatio));
    },
    [direction],
  );

  const handleMouseUp = useCallback(() => {
    dragStateRef.current = null;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!workspaceRef.current) return;
      event.preventDefault();

      const rect = workspaceRef.current.getBoundingClientRect();
      const size = direction === LayoutDirection.Row ? rect.width : rect.height;
      if (size <= 0) return;

      dragStateRef.current = {
        start:
          direction === LayoutDirection.Row ? event.clientX : event.clientY,
        size,
        ratio: splitRatio,
      };
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [workspaceRef, direction, splitRatio, handleMouseMove, handleMouseUp],
  );

  useEffect(() => () => handleMouseUp(), [handleMouseUp]);

  const primaryBasis = `${splitRatio * 100}%`;

  useEffect(() => {
    if (isUpdateDiff) {
      setDirection(LayoutDirection.Column);
    } else {
      setDirection(LayoutDirection.Row);
    }
  }, []);

  return (
    <AppWrapper>
      <NavigateModal />
      <WorkspaceWrapper direction={direction} ref={workspaceRef}>
        <Workspace basis={primaryBasis} grow={0} shrink={0}>
          {isUpdateDiff ? <UpdateDiffView /> : <FileView />}
        </Workspace>
        <ResizeHandle direction={direction} onMouseDown={handleMouseDown}>
          <ResizeGrip direction={direction} className="resize-grip">
            <ResizeGripIndicator direction={direction} />
          </ResizeGrip>
        </ResizeHandle>
        <Workspace grow={1} shrink={1}>
          {textContent ? <Outlet /> : null}
        </Workspace>
      </WorkspaceWrapper>
    </AppWrapper>
  );
};

export default Overview;
